import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { GardenSketchEditor } from "@/components/GardenSketchEditor";
import { parseErrorMessage } from "@/lib/gardenFetch";
import { useAuth } from "@/hooks/useAuth";
import type { GardenSceneFeature, GardenSceneGraph } from "@shared/schema";

type Step = "dimensions" | "sketch" | "preferences" | "candidates" | "review";

interface CatalogEntry {
  slug: string;
  commonName: string;
  family: string;
  category: string;
  spacingInches: number;
  daysToMaturity: number;
  seasonTags: string[];
}

interface GardenMinimal {
  id: string;
  referencePhotoUrl: string | null;
  referencePhotoWidth: number | null;
  referencePhotoHeight: number | null;
  estimatedAreaSqFt: string | null;
  hardinessZone: string | null;
  cardinalSouthDegrees: number | null;
  sceneGraph: GardenSceneGraph | null;
  status: string;
}

interface CandidatesResponse {
  candidates: CatalogEntry[];
  picks: { slug: string; recommendedQuantity: number; rationale: string }[];
  zone: string;
  season: string;
  freeSqFt: number;
}

interface Selection {
  slug: string;
  quantity: number;
}

const PREFERENCE_OPTIONS = [
  { id: "salads", label: "Salads & greens" },
  { id: "italian", label: "Italian cooking" },
  { id: "mexican", label: "Mexican / Tex-Mex" },
  { id: "stir-fry", label: "Stir-fry & Asian dishes" },
  { id: "pickles", label: "Pickling & preserves" },
  { id: "herbs", label: "Fresh herbs" },
  { id: "berries", label: "Berries & fruit" },
  { id: "kid-friendly", label: "Kid-friendly snacks" },
];

const FAMILY_OPTIONS = [
  { id: "solanaceae", label: "Tomatoes / peppers / eggplant" },
  { id: "brassicaceae", label: "Broccoli / kale / cabbage" },
  { id: "cucurbitaceae", label: "Squash / cucumbers / melons" },
  { id: "fabaceae", label: "Beans / peas" },
  { id: "amaryllidaceae", label: "Onions / garlic / chives" },
  { id: "lamiaceae", label: "Basil / mint / rosemary" },
  { id: "asteraceae", label: "Lettuce / sunflowers" },
];

export default function GardenSetup() {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const { data: current } = useQuery<{ garden: GardenMinimal | null }>({
    queryKey: ["/api/garden"],
    queryFn: async () => {
      const res = await fetch("/api/garden", { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const { data: catalogData } = useQuery<{ catalog: CatalogEntry[] }>({
    queryKey: ["/api/garden/catalog"],
    queryFn: async () => {
      const res = await fetch("/api/garden/catalog", { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const [step, setStep] = useState<Step>("dimensions");
  const [gardenInfo, setGardenInfo] = useState<GardenMinimal | null>(null);
  const [worldWidthFt, setWorldWidthFt] = useState("");
  const [worldHeightFt, setWorldHeightFt] = useState("");
  const [southEdge, setSouthEdge] = useState<"top" | "right" | "bottom" | "left">("bottom");
  const [preferredCuisines, setPreferredCuisines] = useState<string[]>([]);
  const [preferredFamilies, setPreferredFamilies] = useState<string[]>([]);
  const [candidates, setCandidates] = useState<CandidatesResponse | null>(null);
  const [selections, setSelections] = useState<Selection[]>([]);
  const [planResult, setPlanResult] = useState<any>(null);

  useEffect(() => {
    if (current?.garden) {
      setGardenInfo(current.garden);
      // Hydrate dimensions from any prior save so reopening the editor
      // doesn't drop the user's earlier work.
      const ext = current.garden.sceneGraph?.gardenExtentFt;
      if (ext) {
        setWorldWidthFt(String(ext.width));
        setWorldHeightFt(String(ext.height));
      }
      const south = current.garden.cardinalSouthDegrees;
      if (south != null) {
        const edge = south === 0 ? "top" : south === 90 ? "right" : south === 270 ? "left" : "bottom";
        setSouthEdge(edge);
      }
    }
  }, [current]);

  const saveSceneMutation = useMutation({
    mutationFn: async (features: GardenSceneFeature[]) => {
      const southDegrees = { top: 0, right: 90, bottom: 180, left: 270 }[southEdge];
      const res = await fetch("/api/garden/scene", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          worldWidthFt: Number(worldWidthFt),
          worldHeightFt: Number(worldHeightFt),
          cardinalSouthDegrees: southDegrees,
          features,
        }),
      });
      if (res.status === 401) {
        queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
        throw new Error("Your session expired — please sign in again.");
      }
      if (!res.ok) throw new Error(await parseErrorMessage(res, "Saving layout failed"));
      return res.json();
    },
    onSuccess: (data) => {
      setGardenInfo(data.garden);
      queryClient.invalidateQueries({ queryKey: ["/api/garden"] });
      setStep("preferences");
    },
  });

  const savePreferencesAndLoadCandidates = useMutation({
    mutationFn: async () => {
      // Preferences are ephemeral until the plan is built; we just pass them
      // forward to candidates via query state. A full persist happens in the
      // plan mutation.
      const res = await fetch("/api/garden/candidates", {
        method: "GET",
        credentials: "include",
      });
      if (!res.ok) throw new Error(await parseErrorMessage(res, "Loading candidates failed"));
      return res.json() as Promise<CandidatesResponse>;
    },
    onSuccess: (data) => {
      setCandidates(data);
      const defaultSelections: Selection[] = data.picks.map((p) => ({
        slug: p.slug,
        quantity: p.recommendedQuantity,
      }));
      setSelections(defaultSelections);
      setStep("candidates");
    },
  });

  const planMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/garden/plan", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ selections }),
      });
      if (!res.ok) throw new Error(await parseErrorMessage(res, "Building plan failed"));
      return res.json();
    },
    onSuccess: (data) => {
      setPlanResult(data);
      queryClient.invalidateQueries({ queryKey: ["/api/garden"] });
      setStep("review");
    },
  });

  const togglePref = (list: string[], setList: (l: string[]) => void, id: string) => {
    if (list.includes(id)) setList(list.filter((x) => x !== id));
    else setList([...list, id]);
  };

  const toggleSelection = (slug: string, quantity: number) => {
    const existing = selections.find((s) => s.slug === slug);
    if (existing) {
      if (quantity <= 0) {
        setSelections(selections.filter((s) => s.slug !== slug));
      } else {
        setSelections(selections.map((s) => (s.slug === slug ? { ...s, quantity } : s)));
      }
    } else if (quantity > 0) {
      setSelections([...selections, { slug, quantity }]);
    }
  };

  const selectionFamilyCount = useMemo(() => {
    const fams = new Set<string>();
    for (const sel of selections) {
      const c = catalogData?.catalog.find((x) => x.slug === sel.slug);
      if (c) fams.add(c.family);
    }
    return fams.size;
  }, [selections, catalogData]);

  const totalPlants = useMemo(
    () => selections.reduce((sum, s) => sum + s.quantity, 0),
    [selections],
  );

  const usedSqFt = useMemo(() => {
    let sqIn = 0;
    for (const sel of selections) {
      const c = catalogData?.catalog.find((x) => x.slug === sel.slug);
      if (c) sqIn += sel.quantity * c.spacingInches * c.spacingInches;
    }
    return sqIn / 144;
  }, [selections, catalogData]);

  const autoFillForYield = () => {
    if (!candidates) return;
    // Maximize yield by filling the free area proportional to recommended
    // quantities, weighted by shortest days-to-maturity (faster turnover =
    // more harvests per season). Cap per-plant at a sensible max.
    const budgetSqFt = Math.max(0, candidates.freeSqFt);
    const scored = candidates.picks.map((p) => {
      const c = catalogData?.catalog.find((x) => x.slug === p.slug);
      const days = c?.daysToMaturity ?? 90;
      const weight = 1 / Math.max(20, days);
      return { slug: p.slug, catalog: c, weight, base: p.recommendedQuantity };
    });
    const totalWeight = scored.reduce((s, r) => s + r.weight, 0) || 1;
    const next: Selection[] = [];
    let remainingSqFt = budgetSqFt;
    for (const row of scored) {
      if (!row.catalog) continue;
      const share = (row.weight / totalWeight) * budgetSqFt;
      const perPlantSqFt = (row.catalog.spacingInches * row.catalog.spacingInches) / 144;
      const qty = Math.max(
        1,
        Math.min(20, Math.floor(share / Math.max(0.25, perPlantSqFt))),
      );
      const actualSqFt = qty * perPlantSqFt;
      if (actualSqFt > remainingSqFt && next.length > 0) break;
      remainingSqFt -= actualSqFt;
      next.push({ slug: row.slug, quantity: qty });
    }
    setSelections(next);
  };

  if (!authLoading && !isAuthenticated) {
    return (
      <div className="mx-auto max-w-2xl space-y-4 p-4 pb-24">
        <h1 className="text-2xl font-semibold">Set up your garden</h1>
        <Card>
          <CardContent className="space-y-3 pt-6">
            <p className="text-sm">
              You're signed out, so saving your garden won't work yet. Sign in and
              come back to this page to continue.
            </p>
            <Button onClick={() => setLocation("/login?next=/garden/setup")}>
              Sign in
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4 p-4 pb-24">
      <h1 className="text-2xl font-semibold">Set up your garden</h1>
      <div className="flex flex-wrap gap-1 text-xs">
        {(["dimensions", "sketch", "preferences", "candidates", "review"] as Step[]).map(
          (s) => (
            <Badge key={s} variant={step === s ? "default" : "outline"}>
              {s}
            </Badge>
          ),
        )}
      </div>

      {step === "dimensions" ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Tell us about your garden</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              You'll sketch your garden's layout on the next step. First we need
              its rough size and which way is south. Type any size that fits
              your space — small herb beds and large yards both work.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Width (east-west)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    inputMode="decimal"
                    step="1"
                    min="1"
                    placeholder="e.g. 30"
                    value={worldWidthFt}
                    onChange={(e) => setWorldWidthFt(e.target.value)}
                  />
                  <span className="text-sm text-muted-foreground">ft</span>
                </div>
              </div>
              <div>
                <Label>Depth (north-south)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    inputMode="decimal"
                    step="1"
                    min="1"
                    placeholder="e.g. 30"
                    value={worldHeightFt}
                    onChange={(e) => setWorldHeightFt(e.target.value)}
                  />
                  <span className="text-sm text-muted-foreground">ft</span>
                </div>
              </div>
            </div>
            <div>
              <Label>Which side is south?</Label>
              <p className="text-xs text-muted-foreground">
                The planner uses this so tall plants don't shade short ones.
              </p>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {(
                  [
                    { id: "top", label: "Top" },
                    { id: "bottom", label: "Bottom" },
                    { id: "left", label: "Left" },
                    { id: "right", label: "Right" },
                  ] as const
                ).map((opt) => (
                  <Button
                    key={opt.id}
                    type="button"
                    size="sm"
                    variant={southEdge === opt.id ? "default" : "outline"}
                    onClick={() => setSouthEdge(opt.id)}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            </div>
            <Button
              disabled={!(Number(worldWidthFt) > 0 && Number(worldHeightFt) > 0)}
              onClick={() => setStep("sketch")}
            >
              Start sketching
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {step === "sketch" ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Sketch your garden</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Drop beds, fences, trees, and structures onto the canvas. Drag to
              move, drag the corner handle to resize. The planner uses this to
              place new plants without overlapping what's already there.
            </p>
            <GardenSketchEditor
              initial={gardenInfo?.sceneGraph ?? null}
              worldWidthFt={Number(worldWidthFt)}
              worldHeightFt={Number(worldHeightFt)}
              cardinalSouthDegrees={
                { top: 0, right: 90, bottom: 180, left: 270 }[southEdge]
              }
              onSave={(features) => saveSceneMutation.mutate(features)}
              saving={saveSceneMutation.isPending}
            />
            {saveSceneMutation.error ? (
              <div className="text-sm text-destructive">
                {String(saveSceneMutation.error)}
              </div>
            ) : null}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep("dimensions")}>
                Back
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {step === "preferences" ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">What do you want to grow?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label>What kinds of cooking do you do?</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {PREFERENCE_OPTIONS.map((opt) => (
                  <Button
                    key={opt.id}
                    type="button"
                    size="sm"
                    variant={preferredCuisines.includes(opt.id) ? "default" : "outline"}
                    onClick={() => togglePref(preferredCuisines, setPreferredCuisines, opt.id)}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <Label>Which plant families are you most drawn to?</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {FAMILY_OPTIONS.map((opt) => (
                  <Button
                    key={opt.id}
                    type="button"
                    size="sm"
                    variant={preferredFamilies.includes(opt.id) ? "default" : "outline"}
                    onClick={() => togglePref(preferredFamilies, setPreferredFamilies, opt.id)}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep("sketch")}>
                Back
              </Button>
              <Button
                disabled={savePreferencesAndLoadCandidates.isPending}
                onClick={() => savePreferencesAndLoadCandidates.mutate()}
              >
                {savePreferencesAndLoadCandidates.isPending
                  ? "Picking plants…"
                  : "See plant suggestions"}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {step === "candidates" && candidates ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Pick your plants</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Zone {candidates.zone} · {candidates.freeSqFt.toFixed(1)} sq ft free ·{" "}
              {selectionFamilyCount} families · {totalPlants} plants ·{" "}
              {usedSqFt.toFixed(1)} sq ft used
              {usedSqFt > candidates.freeSqFt
                ? " (over budget — consider reducing)"
                : ""}
              {selectionFamilyCount < 4 ? " (aim for at least 4 families)" : ""}.
            </p>
            <div>
              <Button type="button" variant="outline" size="sm" onClick={autoFillForYield}>
                Auto-fill for max yield
              </Button>
            </div>
            <ul className="space-y-2">
              {candidates.picks.map((p) => {
                const catalog = catalogData?.catalog.find((c) => c.slug === p.slug);
                const current = selections.find((s) => s.slug === p.slug)?.quantity ?? 0;
                return (
                  <li key={p.slug} className="flex items-start gap-2 rounded border p-3">
                    <div className="flex-1">
                      <div className="font-medium">
                        {catalog?.commonName ?? p.slug}
                        <span className="ml-2 text-xs text-muted-foreground">
                          {catalog?.family} · {catalog?.daysToMaturity}d
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground">{p.rationale}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => toggleSelection(p.slug, Math.max(0, current - 1))}
                      >
                        −
                      </Button>
                      <span className="w-6 text-center text-sm">{current}</span>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => toggleSelection(p.slug, current + 1)}
                      >
                        +
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep("preferences")}>
                Back
              </Button>
              <Button
                disabled={selections.length === 0 || planMutation.isPending}
                onClick={() => planMutation.mutate()}
              >
                {planMutation.isPending ? "Building plan…" : "Generate plan"}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {step === "review" && planResult ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Your plan is ready</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm">
              We scheduled {planResult.plants.length} plants and added reminders for seed
              starting, transplanting, and harvest.
            </p>
            <Button onClick={() => setLocation("/garden")}>Open garden</Button>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
